import clsx from 'clsx';
import { usePostHog } from 'posthog-js/react';
import { PiGlobe, PiMagnifyingGlassBold, PiUserCircle } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { useBoolean } from 'usehooks-ts';
import { PostHogEvent } from '@_/constants';
import { useDialog } from '@_/hooks';
import { Avatar, Link, Skeleton, SkeletonAvatar } from '@_/ui';
import { LoginDialog } from '#src/components/dialogs/LoginDialog.tsx';
import { SelectLocaleDialog } from '#src/components/dialogs/SelectLocaleDialog.tsx';
import { FullScreenGlobalSearch, GlobalSearch } from '#src/components/GlobalSearch/index.ts';
import { Logo } from '#src/components/Logo/index.ts';
import { buttonI18n, navI18n } from '#src/i18nMessages/index.ts';
import { useStores } from '#src/state/StoreContext.tsx';
import { MAX_PAGE_WIDTH } from '#src/theme/constants.ts';
import { useAppShell } from '../useAppShell.ts';
import { useAppShellBoundsWatcher } from '../useAppShellBoundsWatcher.ts';
import { DesktopMainNav } from './DesktopMainNav.tsx';
import { DesktopNavItem } from './DesktopNavItem.tsx';
import { DesktopUserNav } from './DesktopUserNav.tsx';
import { HamburgerButton } from './HamburgerButton.tsx';
import { MobileNav } from './MobileNav.tsx';

export function AppHeader({
  showSearch,
  simple,
  sticky
}: {
  showSearch?: boolean;
  simple?: boolean;
  sticky?: boolean;
}) {
  if (simple) {
    return <SimpleAppHeader />;
  }

  return (
    <>
      <DesktopAppHeader showSearch={showSearch} sticky={sticky} />
      <MobileAppHeader showSearch={showSearch} sticky={sticky} />
    </>
  );
}

function SimpleAppHeader() {
  return (
    <header className="flex h-12 items-end justify-center sm:mb-4 sm:h-16">
      <Link className="block" from="/{-$locale}" to="/{-$locale}" plain>
        <Logo />
      </Link>
    </header>
  );
}

function DesktopAppHeader({ showSearch, sticky }: { showSearch?: boolean; sticky?: boolean }) {
  const appShelBoundsRef = useAppShellBoundsWatcher({ side: 'top' });
  const posthog = usePostHog();
  const { useAuthStore } = useStores();
  const isAuthReady = useAuthStore(state => !!state.lastUpdated);
  const user = useAuthStore(state => state.user);

  const localeDialog = useDialog({
    component: SelectLocaleDialog
  });

  const loginDialog = useDialog({
    component: LoginDialog
  });

  return (
    <>
      {localeDialog.element}
      {loginDialog.element}

      <div
        ref={sticky ? appShelBoundsRef : undefined}
        className={clsx(
          'hidden lg:block',
          'relative z-10 bg-white',
          sticky && 'sticky top-0 border-b border-gray-200'
        )}
      >
        <div className={clsx(MAX_PAGE_WIDTH, 'relative mx-auto h-16 px-8')}>
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              <Link className="mt-px mr-8 block shrink-0" from="/{-$locale}" to="/{-$locale}" plain>
                <Logo />
              </Link>

              {showSearch ? (
                <div className="mr-6 w-72">
                  <GlobalSearch openWidth={560} showSubmitButton={false} size="md" />
                </div>
              ) : (
                <DesktopMainNav />
              )}
            </div>

            <div className="ml-6 flex items-center gap-x-3">
              {isAuthReady && (
                <>
                  {user ? (
                    <DesktopNavItem
                      label={<FormattedMessage {...navI18n.bookings} />}
                      renderLink={props => (
                        <Link {...props} from="/{-$locale}" to="/{-$locale}/bookings" />
                      )}
                    />
                  ) : (
                    <DesktopNavItem
                      label={<FormattedMessage {...buttonI18n.signUp} />}
                      onClick={() => {
                        posthog.capture(PostHogEvent.SignUpButtonClicked);
                        loginDialog.open();
                      }}
                    />
                  )}
                </>
              )}

              <DesktopNavItem icon={<PiGlobe />} onClick={() => localeDialog.open()} subtle />

              <DesktopUserNav />
            </div>
          </div>
        </div>
      </div>

      {showSearch && (
        <div className={clsx('hidden lg:block', 'border-b border-gray-200')}>
          <div className={clsx(MAX_PAGE_WIDTH, 'relative mx-auto px-6 py-2.5')}>
            <DesktopMainNav />
          </div>
        </div>
      )}
    </>
  );
}

function MobileAppHeader({ showSearch, sticky }: { showSearch?: boolean; sticky?: boolean }) {
  const appShellBoundsRef = useAppShellBoundsWatcher({ side: 'top' });
  const { mobileSearch } = useAppShell();
  const { useAuthStore } = useStores();
  const isAuthReady = useAuthStore(state => !!state.lastUpdated);
  const isAuthenticated = useAuthStore(state => !!state.session);
  const user = useAuthStore(state => state.user);

  const { setFalse: closeNav, toggle: toggleNavOpen, value: navOpen } = useBoolean(false);

  return (
    <div
      ref={sticky ? appShellBoundsRef : undefined}
      className={clsx(
        'lg:hidden',
        'relative z-40 bg-white',
        sticky && 'sticky top-0 border-b border-gray-200'
      )}
    >
      <div className="h-14">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
          <Link from="/{-$locale}" to="/{-$locale}" plain>
            <Logo />
          </Link>
        </div>

        <div className="flex h-full items-center justify-between px-4">
          <div className="flex items-center gap-x-4">
            <HamburgerButton onToggle={toggleNavOpen} open={navOpen} />

            {showSearch && !mobileSearch.isOpen && (
              <button
                className="text-base text-gray-500 focus:outline-hidden"
                onClick={() => mobileSearch.open()}
                type="button"
              >
                <PiMagnifyingGlassBold className="size-5" />
              </button>
            )}
          </div>

          <Link
            className="mr-1 block"
            from="/{-$locale}"
            to={user ? '/{-$locale}/settings' : '/{-$locale}/login'}
            plain
          >
            {isAuthReady ? (
              <>
                {isAuthenticated ? (
                  <Avatar className="size-8" name={user?.name} src={user?.avatarUrl} />
                ) : (
                  <PiUserCircle className="size-8 text-gray-700" />
                )}
              </>
            ) : (
              <Skeleton>
                <SkeletonAvatar className="size-8" />
              </Skeleton>
            )}
          </Link>
        </div>
      </div>

      <MobileNav onClose={closeNav} open={navOpen} />

      {mobileSearch.isOpen && <FullScreenGlobalSearch onClose={mobileSearch.close} />}
    </div>
  );
}
